import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import api from '../services/api';

function GeneralSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});
  const [retentionDays, setRetentionDays] = useState('');
  const [maxUploadSizeGB, setMaxUploadSizeGB] = useState('');
  const [sentryDsn, setSentryDsn] = useState('');
  const [sentryEnv, setSentryEnv] = useState('');
  const [sentryRate, setSentryRate] = useState('');
  const [backupEnabled, setBackupEnabled] = useState(true);
  const [backupHour, setBackupHour] = useState('');
  const [backupRetention, setBackupRetention] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings/');
      // Convert array to object for easier access
      const settingsObj = {};
      response.data.forEach(setting => {
        settingsObj[setting.key] = setting;
      });
      setSettings(settingsObj);
      // Set the retention days local state
      if (settingsObj.job_retention_days) {
        setRetentionDays(settingsObj.job_retention_days.value.toString());
      }
      // Set the upload size local state (convert MB to GB)
      if (settingsObj.max_upload_size_mb) {
        const sizeInGB = (settingsObj.max_upload_size_mb.value / 1024).toFixed(1);
        setMaxUploadSizeGB(sizeInGB);
      }
      // Set Sentry settings
      if (settingsObj.sentry_dsn) {
        setSentryDsn(settingsObj.sentry_dsn.value || '');
      }
      if (settingsObj.sentry_environment) {
        setSentryEnv(settingsObj.sentry_environment.value || 'production');
      }
      if (settingsObj.sentry_traces_sample_rate) {
        setSentryRate((settingsObj.sentry_traces_sample_rate.value * 100).toString());
      }
      // Set backup settings
      if (settingsObj.backup_enabled) {
        setBackupEnabled(settingsObj.backup_enabled.value);
      }
      if (settingsObj.backup_hour) {
        setBackupHour(settingsObj.backup_hour.value.toString());
      }
      if (settingsObj.backup_retention_days) {
        setBackupRetention(settingsObj.backup_retention_days.value.toString());
      }
      setError('');
    } catch (_err) {
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await api.put(`/api/settings/${key}`, { value });
      setSettings(prev => ({
        ...prev,
        [key]: { ...prev[key], value }
      }));
      // Update local state for retention days
      if (key === 'job_retention_days') {
        setRetentionDays(value.toString());
      }
      // Update local state for upload size
      if (key === 'max_upload_size_mb') {
        const sizeInGB = (value / 1024).toFixed(1);
        setMaxUploadSizeGB(sizeInGB);
      }
      setError('');
    } catch (_err) {
      setError(`Failed to update ${key}`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Registration Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Registration
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.registration_enabled?.value || false}
                    onChange={(e) => updateSetting('registration_enabled', e.target.checked)}
                    disabled={saving.registration_enabled}
                  />
                }
                label="Allow new user registration"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                When disabled, only administrators can create new user accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* URLs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>URLs</Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                size="small"
                label="Public app URL"
                helperText="Used for share links, KDM request links, and email branding"
                value={settings.public_app_url?.value || ''}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, public_app_url: { ...prev.public_app_url, value: e.target.value } }));
                }}
                onBlur={(e) => updateSetting('public_app_url', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Encoder agent backend URL"
                helperText="Internal URL the watermark encoder agents use to reach this backend (e.g. http://192.168.1.30:8000). Encoders set this once at install time; changing it here affects only the install command shown in the encoder setup dialog."
                value={settings.agent_backend_url?.value || ''}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, agent_backend_url: { ...prev.agent_backend_url, value: e.target.value } }));
                }}
                onBlur={(e) => updateSetting('agent_backend_url', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                File Upload
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  id="max-upload-size"
                  name="maxUploadSize"
                  type="number"
                  label="Maximum upload size (GB)"
                  value={maxUploadSizeGB}
                  onChange={(e) => {
                    setMaxUploadSizeGB(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const valueInGB = parseFloat(maxUploadSizeGB) || 0;
                      if (valueInGB >= 0) {
                        const valueInMB = Math.round(valueInGB * 1024);
                        updateSetting('max_upload_size_mb', valueInMB);
                      }
                    }
                  }}
                  disabled={saving.max_upload_size_mb}
                  size="small"
                  inputProps={{ min: 0, step: 0.1 }}
                  sx={{ width: 200 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const valueInGB = parseFloat(maxUploadSizeGB) || 0;
                    if (valueInGB >= 0) {
                      const valueInMB = Math.round(valueInGB * 1024);
                      updateSetting('max_upload_size_mb', valueInMB);
                    }
                  }}
                  disabled={saving.max_upload_size_mb || maxUploadSizeGB === ((settings.max_upload_size_mb?.value || 5120) / 1024).toFixed(1)}
                >
                  Save
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Controls the maximum file size for video uploads
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Retention */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Retention
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  id="job-retention-days"
                  name="jobRetentionDays"
                  type="number"
                  label="Job retention (days)"
                  value={retentionDays}
                  onChange={(e) => {
                    setRetentionDays(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const value = parseInt(retentionDays) || 0;
                      if (value >= 0) {
                        updateSetting('job_retention_days', value);
                      }
                    }
                  }}
                  disabled={saving.job_retention_days}
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{ width: 200 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const value = parseInt(retentionDays) || 0;
                    if (value >= 0) {
                      updateSetting('job_retention_days', value);
                    }
                  }}
                  disabled={saving.job_retention_days || retentionDays === (settings.job_retention_days?.value || 30).toString()}
                >
                  Save
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Completed jobs older than this will be automatically deleted.
                Set to 0 to keep all data indefinitely.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Error Monitoring Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Error Monitoring (Sentry)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  id="sentry-dsn"
                  name="sentryDsn"
                  label="Sentry DSN"
                  value={sentryDsn}
                  onChange={(e) => setSentryDsn(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="https://xxx@xxx.ingest.sentry.io/xxx"
                  helperText="Leave empty to disable error monitoring"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  id="sentry-environment"
                  name="sentryEnvironment"
                  label="Environment"
                  value={sentryEnv}
                  onChange={(e) => setSentryEnv(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="production"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  id="sentry-rate"
                  name="sentryRate"
                  type="number"
                  label="Sample Rate (%)"
                  value={sentryRate}
                  onChange={(e) => setSentryRate(e.target.value)}
                  size="small"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ width: 200 }}
                  helperText="Percentage of transactions to monitor"
                />
              </Box>
              
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  // Save all Sentry settings
                  await updateSetting('sentry_dsn', sentryDsn);
                  await updateSetting('sentry_environment', sentryEnv);
                  await updateSetting('sentry_traces_sample_rate', parseFloat(sentryRate) / 100);
                  
                  // Apply settings
                  try {
                    await api.post('/api/settings/apply-sentry');
                    setError('');
                    alert('Sentry settings applied successfully. Backend service restarted.');
                  } catch (_err) {
                    setError('Failed to apply Sentry settings');
                  }
                }}
                disabled={saving.sentry_dsn || saving.sentry_environment || saving.sentry_traces_sample_rate}
              >
                Save & Apply
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Sign up for free at <a href="https://sentry.io" target="_blank" rel="noopener noreferrer">sentry.io</a> to get a DSN
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Backup Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Backups
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={backupEnabled}
                      onChange={(e) => setBackupEnabled(e.target.checked)}
                    />
                  }
                  label="Enable automatic backups"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  id="backup-hour"
                  name="backupHour"
                  type="number"
                  label="Backup time (hour)"
                  value={backupHour}
                  onChange={(e) => setBackupHour(e.target.value)}
                  disabled={!backupEnabled}
                  size="small"
                  inputProps={{ min: 0, max: 23 }}
                  sx={{ width: 200 }}
                  helperText="Hour of day (0-23) to run backup"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  id="backup-retention"
                  name="backupRetention"
                  type="number"
                  label="Retention days"
                  value={backupRetention}
                  onChange={(e) => setBackupRetention(e.target.value)}
                  disabled={!backupEnabled}
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{ width: 200 }}
                  helperText="Number of days to keep backups"
                />
              </Box>
              
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  // Save all backup settings
                  await updateSetting('backup_enabled', backupEnabled);
                  await updateSetting('backup_hour', parseInt(backupHour) || 2);
                  await updateSetting('backup_retention_days', parseInt(backupRetention) || 30);
                  
                  // Update cron job
                  try {
                    await api.post('/api/settings/update-backup-cron');
                    setError('');
                    alert('Backup settings updated successfully.');
                  } catch (_err) {
                    setError('Failed to update backup settings');
                  }
                }}
                disabled={saving.backup_enabled || saving.backup_hour || saving.backup_retention_days}
              >
                Save & Apply
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Backups are stored in /opt/watermarked-screeners/backups/
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default GeneralSettings;